import { CircleAlert, CircleCheck, Eye } from "lucide-react";
import { Fragment, memo, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn, pluralize } from "@/lib/utils";
import { DOC_TYPE_LABELS, DOC_TYPES, type DocType } from "@/types/statement";

import { agentClient } from "../agentClient";
import { canPreview, contentTypeFromName } from "../chat/FilePreviewDialog";
import type { WireChoice } from "../protocol/protocol";
import type { AgentState } from "../store/agentSlice";
import { selectComposerFiling } from "../store/selectors";
import { AssistantCard, AssistantCardFooter, AssistantCardHeader } from "../theme";
import {
  choiceOptions,
  filingDecideItems,
  filingItems,
  isDeclineOption,
  type FilingDecideItem,
  type FilingItem,
  type FilingTypes,
} from "./wire";

/**
 * The thread's batched filing on the composer (not a message part): the files the agent suggests
 * filing, one document type each (the agent's, which the user can change), confirmed in one go.
 * Confirming files every ticked file not already filed. After a confirmation, or when every file
 * was already filed, one receipt line.
 */
// Memoised: it sits in the composer, which re-renders on every keystroke.
export const FilingPopup = memo(function FilingPopup({
  threadId,
  className,
  onSettled,
  onPreview,
}: {
  threadId: number | null;
  /** For the open card; the receipt keeps its own layout. */
  className?: string;
  /** After a decision: the card gives way to its receipt, so focus goes back to the composer. */
  onSettled?: () => void;
  /** A file's eye; files get none without it. */
  onPreview?: (item: FilingItem) => void;
}) {
  const choice = useSelector((state: { agent: AgentState }) =>
    selectComposerFiling(state, threadId)
  );
  if (!choice) return null;
  if (choice.status !== "pending") return <FilingReceipt choice={choice} />;
  return (
    <OpenFiling
      key={choice.ref}
      choice={choice}
      className={className}
      onSettled={onSettled}
      onPreview={onPreview}
    />
  );
});

/** The application every file goes to, or null when they go to several (or it has no name). */
function oneApplication(items: FilingItem[]): string | null {
  const labels = new Set(items.map((item) => item.applicationLabel));
  const [label] = labels;
  return labels.size === 1 && label ? label : null;
}

function OpenFiling({
  choice,
  className,
  onSettled,
  onPreview,
}: {
  choice: WireChoice;
  className?: string;
  onSettled?: () => void;
  onPreview?: (item: FilingItem) => void;
}) {
  const items = useMemo(() => filingItems(choice), [choice]);
  const [types, setTypes] = useState<FilingTypes>({});
  /** The files the user unticked; the server's when it already left some out. */
  const [leftOut, setLeftOut] = useState<ReadonlySet<number>>(
    () => new Set(items.filter((item) => !item.include).map((item) => item.threadFileId))
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileable = items.filter((item) => !item.alreadyFiled);
  const count = fileable.filter((item) => !leftOut.has(item.threadFileId)).length;
  const application = oneApplication(items);
  const options = choiceOptions(choice);
  const confirm = fileable.length > 0 && options.find((option) => !isDeclineOption(option.id));
  const dismiss = options.find((option) => isDeclineOption(option.id));

  const include = (threadFileId: number, included: boolean) =>
    setLeftOut((current) => {
      const next = new Set(current);
      if (included) next.delete(threadFileId);
      else next.add(threadFileId);
      return next;
    });

  const decide = (optionId: string, decideItems?: FilingDecideItem[]) => {
    setBusy(true);
    setError(null);
    agentClient
      .decide(choice.ref, optionId, decideItems)
      .then(onSettled, (reason: { message?: string }) => {
        setBusy(false);
        setError(reason?.message || "That didn't go through. Try again.");
      });
  };

  return (
    // The rows lay out by the card's own width (the composer's), not the window's.
    <AssistantCard data-agent-filing className={cn("[container-type:inline-size]", className)}>
      <AssistantCardHeader title={application ? `File these on ${application}?` : "File these?"} />
      <ul>
        {items.map((item) => {
          const included = !item.alreadyFiled && !leftOut.has(item.threadFileId);
          const previewable = !!onPreview && canPreview(contentTypeFromName(item.name));
          // A label only around a tick box, so a tap on the name ticks it too.
          const NameCell = item.alreadyFiled ? "div" : "label";
          return (
            <li
              key={item.threadFileId}
              // Narrow (the card, not the window): the name, its tick and eye on one line, the type full
              // width below it.
              className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 border-t border-border/60 px-3.5 py-2 text-[13px] [@container(min-width:26rem)]:flex-nowrap"
            >
              {/* The eye follows the name, level with its first line. */}
              <div className="flex min-w-0 flex-1 items-start gap-2.5 [@container(min-width:26rem)]:flex-initial">
                <NameCell className="flex min-w-0 flex-1 items-center gap-2.5 [@container(min-width:26rem)]:flex-initial">
                  {item.alreadyFiled ? (
                    <span className="w-4 shrink-0 max-sm:w-5" aria-hidden />
                  ) : (
                    <Checkbox
                      checked={included}
                      disabled={busy}
                      onCheckedChange={(checked) => include(item.threadFileId, checked === true)}
                      className="max-sm:h-5 max-sm:w-5"
                    />
                  )}
                  <span className="flex min-w-0 flex-col">
                    <span
                      className={cn(
                        "break-words font-medium",
                        !included && "text-muted-foreground"
                      )}
                    >
                      <FileName name={item.name} />
                    </span>
                    {!application && item.applicationLabel && (
                      <span className="text-xs text-muted-foreground">{item.applicationLabel}</span>
                    )}
                  </span>
                </NameCell>
                {previewable && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    // The negative margins keep the tap target but take up one line of the name.
                    className="-my-0.5 h-6 w-6 shrink-0 text-muted-foreground max-sm:-my-2 max-sm:h-9 max-sm:w-9"
                    aria-label={`Preview ${item.name}`}
                    onClick={() => onPreview(item)}
                  >
                    <Eye className="!size-3.5" />
                  </Button>
                )}
              </div>
              <FileType
                item={item}
                type={types[item.threadFileId] ?? item.docType}
                disabled={busy}
                onChange={(docType) =>
                  setTypes((current) => ({ ...current, [item.threadFileId]: docType }))
                }
              />
            </li>
          );
        })}
      </ul>
      {error && (
        <p role="alert" className="px-3.5 pb-2 text-xs text-destructive">
          {error}
        </p>
      )}
      <AssistantCardFooter className="border-t border-border/60 pt-3">
        {confirm && (
          <Button
            size="sm"
            className="aria-disabled:opacity-50 max-sm:h-10"
            // aria-disabled while busy, not disabled: focus stays here while the decision goes
            // through. Disabled with nothing ticked.
            aria-disabled={busy}
            disabled={count === 0}
            onClick={() => !busy && decide(confirm.id, filingDecideItems(items, types, leftOut))}
          >
            {/* Nothing ticked: the disabled button names no count ("File 0 documents"). */}
            {count === 0 ? "File" : `File ${pluralize(count, "document")}`}
          </Button>
        )}
        {dismiss && (
          <Button
            size="sm"
            variant="ghost"
            className="aria-disabled:opacity-50 max-sm:h-10"
            aria-disabled={busy}
            onClick={() => !busy && decide(dismiss.id)}
          >
            Not now
          </Button>
        )}
      </AssistantCardFooter>
    </AssistantCard>
  );
}

/** The file's one document type: the same list as Application > Documents. */
function FileType({
  item,
  type,
  disabled,
  onChange,
}: {
  item: FilingItem;
  type: DocType | null;
  disabled: boolean;
  onChange: (docType: DocType) => void;
}) {
  if (item.alreadyFiled) {
    return (
      // On the right at every width, level with the name's first line.
      <Badge className="ml-auto w-fit shrink-0 self-start border-transparent bg-warning-soft text-warning">
        Already filed
      </Badge>
    );
  }
  return (
    <Select
      // Empty while the file has no type yet: the select stays controlled once one is picked.
      value={type ?? ""}
      disabled={disabled}
      onValueChange={(value) => onChange(value as DocType)}
    >
      <SelectTrigger
        aria-label={`Document type for ${item.name}`}
        className="h-8 w-full shrink-0 text-xs max-sm:h-10 [@container(min-width:26rem)]:ml-auto [@container(min-width:26rem)]:w-[180px]"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {DOC_TYPES.map((docType) => (
          <SelectItem key={docType} value={docType} className="text-xs">
            {DOC_TYPE_LABELS[docType]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Room for any real extension: the dot and up to 8 characters. */
const MAX_EXTENSION_LENGTH = 9;

/** A file name that wraps after its hyphens, spaces and dots, never inside or before a short extension. */
function FileName({ name }: { name: string }) {
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  return (
    <>
      {stem.split(".").map((part, index) => (
        <Fragment key={index}>
          {index > 0 && (
            <>
              .<wbr />
            </>
          )}
          {part}
        </Fragment>
      ))}
      {dot > 0 &&
        // A long "extension" is part of the name and may wrap like the rest of it.
        (name.length - dot <= MAX_EXTENSION_LENGTH ? (
          <span className="whitespace-nowrap">{name.slice(dot)}</span>
        ) : (
          name.slice(dot)
        ))}
    </>
  );
}

/**
 * What the confirmation did, in one line: what it filed and what it could not. A success only
 * when nothing was refused; a partial filing reads as a warning.
 */
function receipt(items: FilingItem[]): { line: string; complete: boolean } {
  const filed = items.filter((item) => item.outcome === "filed").length;
  const refused = items.filter((item) => item.outcome === "refused").length;
  const application = oneApplication(items);
  const on = application ? ` on ${application}` : "";
  const notFiled = refused ? `, ${refused} not filed` : "";
  if (filed > 0)
    return {
      line: `${pluralize(filed, "document")} filed${on}${notFiled}`,
      complete: refused === 0,
    };
  if (refused > 0)
    return { line: `${pluralize(refused, "document")} not filed${on}`, complete: false };
  return { line: `Already filed${on}`, complete: true };
}

/** Centred above the composer, as wide as its line (02c-web-done). */
function FilingReceipt({ choice }: { choice: WireChoice }) {
  const { line, complete } = receipt(filingItems(choice));
  const Icon = complete ? CircleCheck : CircleAlert;
  return (
    <div
      role="status"
      className={cn(
        "mx-auto mb-4 flex w-fit max-w-full items-center gap-2 rounded-[10px] px-3.5 py-2 text-[12.5px] font-medium max-sm:mb-3",
        complete ? "bg-success-soft text-success" : "bg-warning-soft text-warning"
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {line}
    </div>
  );
}
